import { Link } from "react-router-dom";
import { ArrowRight, Ear, Wind, Activity, HeartPulse, Stethoscope } from "lucide-react";
import { PROCEDURES } from "@/data/procedures";
import { SITE_URL } from "@/data/site";
import { SectionHeading, Breadcrumbs, CTABand } from "@/components/Shared";
import { Seo, breadcrumbSchema } from "@/components/Seo";

const PROC_ICONS = {
  "Nose & Sinus": Wind,
  Ear: Ear,
  Throat: Activity,
  "Head & Neck": HeartPulse,
  "Emergency ENT": Stethoscope,
};

const CATEGORIES = ["Ear", "Nose & Sinus", "Throat", "Head & Neck", "Emergency ENT"];

export default function SurgeryGuide() {
  return (
    <div data-testid="surgery-guide-page">
      <Seo
        title="ENT Surgery Guide — Procedures Explained | Dr. Sanjay Teza, Varanasi"
        description="Detailed patient guides to ENT surgeries in Varanasi: FESS, septoplasty, tympanoplasty, mastoidectomy, tonsillectomy, thyroid surgery and more. Overview, symptoms, diagnosis, procedure & recovery by Dr. Sanjay Teza."
        path="/surgery-guide"
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "ENT Surgery Guide", path: "/surgery-guide" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "ENT Surgical Procedures",
            itemListElement: PROCEDURES.map((p, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: p.name,
              url: `${SITE_URL}/surgery-guide/${p.slug}`,
            })),
          },
        ]}
      />
      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "ENT Surgery Guide" }]} />
          <div className="mt-6">
            <SectionHeading
              eyebrow="ENT Surgery Guide"
              title="Understand Your ENT Surgery — Clearly and Confidently"
              subtitle="Detailed, patient-friendly guides to the ear, nose, throat, head and neck surgeries performed by Dr. Sanjay Teza. Each guide covers the overview, symptoms, diagnosis, procedure, recovery and FAQs."
            />
          </div>
        </div>
      </section>

      <section className="bg-brand-bg">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          {CATEGORIES.map((cat) => {
            const items = PROCEDURES.filter((p) => p.category === cat);
            if (!items.length) return null;
            const Icon = PROC_ICONS[cat] || Stethoscope;
            return (
              <div key={cat} className="mb-14 last:mb-0" data-testid={`category-${cat.toLowerCase().replace(/[\s&]+/g, "-")}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-brand-primary text-white">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h2 className="font-heading text-xl font-bold text-brand-ink">
                    {cat} Surgery
                  </h2>
                </div>
                <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {items.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/surgery-guide/${p.slug}`}
                      data-testid={`guide-card-${p.slug}`}
                      className="group flex h-full flex-col rounded-lg border border-brand-line bg-white p-6 transition-transform duration-200 hover:-translate-y-1 hover:border-brand-primary hover:shadow-[0_10px_30px_rgba(11,27,61,0.08)]"
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="font-heading text-lg font-bold text-brand-ink">
                          {p.short}
                        </h3>
                      </div>
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-brand-slate">
                        {p.tagline}
                      </p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary">
                        Read full guide{" "}
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <CTABand
        title="Not sure which procedure you need?"
        subtitle="Share your symptoms with Dr. Sanjay Teza on WhatsApp and get honest guidance on whether surgery is right for you."
      />
    </div>
  );
}
